/**
 * Toast Notification Component
 *
 * Displays temporary notification messages for success, error, warning, and info states.
 */
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../styles/toast.css';

// Toast types
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Single Toast Item
function ToastItem({ id, type, title, message, duration, onClose }) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(id), 300);
  };

  const getIcon = () => {
    switch (type) {
      case TOAST_TYPES.SUCCESS:
        return 'fa-check-circle';
      case TOAST_TYPES.ERROR:
        return 'fa-times-circle';
      case TOAST_TYPES.WARNING:
        return 'fa-exclamation-triangle';
      case TOAST_TYPES.INFO:
      default:
        return 'fa-info-circle';
    }
  };

  return (
    <div className={`toast-item ${type} ${isExiting ? 'exiting' : ''}`}>
      <div className="toast-icon">
        <i className={`fas ${getIcon()}`}></i>
      </div>
      <div className="toast-content">
        {title && <div className="toast-title">{title}</div>}
        <div className="toast-message">{message}</div>
      </div>
      <button className="toast-close" onClick={handleClose}>
        <i className="fas fa-times"></i>
      </button>
    </div>
  );
}

// Toast Container Component
function ToastContainer({ toasts, removeToast }) {
  if (toasts.length === 0) return null;

  return createPortal(
    <div className="toast-container">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          {...toast}
          onClose={removeToast}
        />
      ))}
    </div>,
    document.body
  );
}

// Custom hook for toast management
let toastIdCounter = 0;
let globalAddToast = null;

export function useToast() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ type = TOAST_TYPES.INFO, title, message, duration = 5000 }) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, type, title, message, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback((message, title = 'Success') =>
    addToast({ type: TOAST_TYPES.SUCCESS, title, message }), [addToast]);

  const error = useCallback((message, title = 'Error') =>
    addToast({ type: TOAST_TYPES.ERROR, title, message, duration: 8000 }), [addToast]);

  const warning = useCallback((message, title = 'Warning') =>
    addToast({ type: TOAST_TYPES.WARNING, title, message }), [addToast]);

  const info = useCallback((message, title = 'Info') =>
    addToast({ type: TOAST_TYPES.INFO, title, message }), [addToast]);

  // Site reference for global access
  useEffect(() => {
    globalAddToast = { success, error, warning, info, addToast };
  }, [success, error, warning, info, addToast]);

  return {
    toasts,
    addToast,
    removeToast,
    clearAll,
    success,
    error,
    warning,
    info,
    ToastContainer: () => <ToastContainer toasts={toasts} removeToast={removeToast} />,
  };
}

// Global toast function (can be used outside of React components)
export const toast = {
  success: (message, title) => globalAddToast?.success(message, title),
  error: (message, title) => globalAddToast?.error(message, title),
  warning: (message, title) => globalAddToast?.warning(message, title),
  info: (message, title) => globalAddToast?.info(message, title),
};

export default ToastContainer;
